import { useBreakpointValue } from "@chakra-ui/react";
import { cn } from "@peerigon/pupper/tailwind";
import {
	Carousel,
	CarouselControls,
	CarouselItem,
} from "src/components/Carousel/Carousel";
import { Product } from "src/components/Product/Product";
import {
	StructuredText,
	StructuredTextData,
	isStructuredText,
} from "src/components/StructuredText/StructuredText";
import { readFromEnumFactory } from "src/lib/utils";
import { useGetProductsFromStructuredText } from "./utils";

const variants = ["static", "carousel"] as const;
const introAlignments = ["left", "center"] as const;
type Variant = (typeof variants)[number];
type IntroAlignment = (typeof introAlignments)[number];

export const readVariant = readFromEnumFactory(variants, "static");
export const readIntroAlignment = readFromEnumFactory(
	introAlignments,
	"center",
);

type ProductComparisonProps = {
	id: string;
	introAlignment: IntroAlignment;
	structuredText: StructuredTextData;
	variant: Variant;
};

export const ProductComparison: React.FC<ProductComparisonProps> = ({
	id,
	introAlignment,
	structuredText,
	variant,
}) => {
	const { intro, products } =
		useGetProductsFromStructuredText(structuredText);

	const isCarousel = useBreakpointValue(
		{
			base: products.length >= 2,
			lg: products.length >= 3,
			xl: products.length >= 4,
		},
		{
			fallback: "base",
		},
	);

	if (variant !== "static" && isCarousel) {
		return (
			<div
				className={cn(
					"pt-4 lg:pt-12 pb-4 lg:pb-12 text-brand-blue bg-brand-light-bright",
					"**:data-[structured-text-block=product]:h-full",
					"[&_.swiper-wrapper]:my-16",
					"[&_.swiper-slide]:shadow-none! [&_.swiper-slide]:w-76! sm:[&_.swiper-slide]:w-md! md:[&_.swiper-slide]:w-lg!",
					introAlignment === "left" ? "text-left" : "text-center",
				)}
			>
				<div className="py-2 px-4 max-w-(--breakpoint-lg) m-auto [&>h1]:max-w-(--breakpoint-md) [&>h1]:m-auto [&>h2]:max-w-(--breakpoint-md) [&>h2]:m-auto">
					{intro && <StructuredText data={intro} />}
				</div>

				<Carousel
					id={id}
					autoplay={{
						delay: 8000,
						disableOnInteraction: true,
					}}
					control={<CarouselControls />}
				>
					{products.map(
						({
							id,
							startingPrice,
							description,
							linkedProduct,
							...rest
						}) => {
							return (
								<CarouselItem key={id}>
									<Product
										startingPrice={startingPrice ?? 0}
										description={
											isStructuredText(description)
												? description
												: undefined
										}
										data-structured-text-block="product"
										{...rest}
									/>
								</CarouselItem>
							);
						},
					)}
				</Carousel>
			</div>
		);
	}

	return (
		<div className="bg-brand-light-bright pt-4 lg:pt-12 text-center text-brand-blue">
			{intro && (
				<div
					className={cn(
						"max-w-(--breakpoint-lg) py-2 m-auto [&>h1]:max-w-(--breakpoint-md) [&>h1]:m-auto [&>h2]:max-w-(--breakpoint-md) [&>h2]:m-auto",
						introAlignment === "left" ? "text-left" : "text-center",
					)}
				>
					<StructuredText data={intro} />
				</div>
			)}
			<div
				className={cn(
					"max-w-(--breakpoint-xl) m-auto py-2 lg:py-4 px-6",
					products.length === 1
						? "flex justify-center"
						: "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3",
				)}
			>
				{products.map(
					({
						id,
						startingPrice,
						description,
						linkedProduct,
						...rest
					}) => {
						return (
							<Product
								key={id}
								startingPrice={startingPrice ?? 0}
								description={
									isStructuredText(description)
										? description
										: undefined
								}
								data-structured-text-block="product"
								{...rest}
							/>
						);
					},
				)}
			</div>
		</div>
	);
};

// 🔬 TBD: Please evaluate
